/**
 * Cashflow Dashboard (v2) — data layer. Calls the fold engine via the shared axios
 * service (Bearer token auto-attached). See docs/DASHBOARD_DESIGN_BRIEF.md, Part 9.
 */
import type { ApiResponse } from '../../services/api';
import { apiService } from '../../services/api';
import { getCockpitData } from '../cashflow/data';
import { buildDepth, Depth } from './depth';
import type { FoldDetail, FoldListResponse } from './types';

/** GET /recon/fold — the invoice list + summary tiles (Screen 1). */
export async function getFoldList(params?: {
	leak?: string;
	state?: string;
	limit?: number;
}): Promise<FoldListResponse> {
	const q = new URLSearchParams();
	if (params?.leak) q.set('leak', params.leak);
	if (params?.state) q.set('state', params.state);
	q.set('limit', String(params?.limit ?? 1000));
	const res = (await apiService.get(`/recon/fold?${q.toString()}`)) as ApiResponse<FoldListResponse>;
	return res.data;
}

/** GET /recon/master — the heavy depth payload (reuses the Cockpit's mapper). */
export async function getDepth(): Promise<Depth> {
	const d = await getCockpitData();
	return buildDepth(d.invoices, d.summary);
}

/** POST /recon/feedback — human correction on the AI analysis (created_by set server-side). */
export interface FeedbackPayload {
	invoice_number: string;
	thread_id?: string | null;
	feedback_type: string;
	suggested_class?: string | null;
	note?: string;
	context?: Record<string, unknown>;
}
export async function postFeedback(payload: FeedbackPayload): Promise<{ id: number; status: string }> {
	const res = (await apiService.post('/recon/feedback', payload)) as ApiResponse<{ id: number; status: string }>;
	return res.data;
}

/** GET /recon/fold/:number — one invoice's full detail (Screen 2). */
export async function getFoldDetail(invoiceNumber: string): Promise<FoldDetail> {
	// invoice numbers contain '/', which the (*) route param handles; encode the rest.
	const res = (await apiService.get(
		`/recon/fold/${invoiceNumber}`
	)) as ApiResponse<FoldDetail>;
	return res.data;
}
